/**
 * Event queue scheduling and processing for Chaos Chess board events.
 *
 * All functions are pure — they take and return GameState (or sub-types).
 * No DOM, no side effects.
 *
 * ## Scheduling
 * Events fire on a fixed cadence: every EVENT_CADENCE turns.
 * The queue always holds MAX_QUEUED_EVENTS upcoming events so the UI can
 * show a full countdown for both the next and the one after.
 *
 * ## Firing
 * At each turn boundary, events with `firesOnTurn <= turnNumber` are applied
 * (in queue order). After firing, a new event is scheduled EVENT_CADENCE
 * turns after the last queued event, keeping the queue full.
 */

import type { GameState, BoardEvent, EventType } from './types'
import { applyOuterRingRotation, applyCenterRotation, applyDiagonalShift } from './transforms'

// ── Constants ─────────────────────────────────────────────────────────────────

/** Fixed number of turns between consecutive events. */
export const EVENT_CADENCE = 7
/** Maximum number of events held in the queue at once. */
export const MAX_QUEUED_EVENTS = 2

const EVENT_TYPES: EventType[] = ['outer_ring_cw', 'outer_ring_ccw', 'center_180', 'diagonal_ne', 'diagonal_sw', 'diagonal_nw', 'diagonal_se']

// ── Internal helpers ──────────────────────────────────────────────────────────

let _idSeq = 0

function newEventId(): string {
  return `ev_${++_idSeq}`
}

function randomEventType(): EventType {
  return EVENT_TYPES[Math.floor(Math.random() * EVENT_TYPES.length)]
}

/**
 * Create a new event scheduled to fire exactly `cadence` turns after `afterTurn`.
 */
function makeEvent(afterTurn: number, cadence: number): BoardEvent {
  return {
    id: newEventId(),
    type: randomEventType(),
    firesOnTurn: afterTurn + cadence,
    announced: false,
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Generate the initial event queue for a new game.
 * Creates MAX_QUEUED_EVENTS events, each EVENT_CADENCE turns apart.
 * Called by buildInitialState when eventsEnabled is true.
 */
export function generateInitialEvents(startTurn: number, cadence = EVENT_CADENCE): BoardEvent[] {
  const queue: BoardEvent[] = []
  let lastTurn = startTurn
  for (let i = 0; i < MAX_QUEUED_EVENTS; i++) {
    const event = makeEvent(lastTurn, cadence)
    queue.push(event)
    lastTurn = event.firesOnTurn
  }
  return queue
}

/**
 * Apply the board transform for a single event to the given state.
 */
export function applyEventTransform(state: GameState, event: BoardEvent): GameState {
  switch (event.type) {
    case 'outer_ring_cw':  return applyOuterRingRotation(state, 'CW')
    case 'outer_ring_ccw': return applyOuterRingRotation(state, 'CCW')
    case 'center_180':     return applyCenterRotation(state)
    case 'diagonal_ne':    return applyDiagonalShift(state, 'NE')
    case 'diagonal_sw':    return applyDiagonalShift(state, 'SW')
    case 'diagonal_nw':    return applyDiagonalShift(state, 'NW')
    case 'diagonal_se':    return applyDiagonalShift(state, 'SE')
  }
}

/**
 * Process the event queue at a turn boundary (called after turnNumber advances).
 *
 * Steps:
 * 1. Fire all events with `firesOnTurn <= state.turnNumber` (apply transforms).
 * 2. Remove fired events; schedule new ones to maintain MAX_QUEUED_EVENTS.
 *
 * Returns `[newState, firedEvents]`.
 * If eventQueue is empty (events disabled), returns the state unchanged.
 */
export function processEventQueue(state: GameState): [GameState, BoardEvent[]] {
  if (state.eventQueue.length === 0) return [state, []]

  const { turnNumber, eventQueue, eventCadence } = state
  const cadence = eventCadence ?? EVENT_CADENCE

  // 1. Partition events: due vs still pending
  const toFire = eventQueue.filter(e => e.firesOnTurn <= turnNumber)
  let remaining = eventQueue.filter(e => e.firesOnTurn > turnNumber)

  // 2. Apply transforms (atomic within each event, sequential across events)
  let newState = state
  for (const event of toFire) {
    newState = applyEventTransform(newState, event)
  }

  // 3. Replenish queue up to MAX_QUEUED_EVENTS at fixed cadence
  while (remaining.length < MAX_QUEUED_EVENTS) {
    const lastTurn = remaining.length > 0
      ? Math.max(...remaining.map(e => e.firesOnTurn))
      : turnNumber
    remaining = [...remaining, makeEvent(lastTurn, cadence)]
  }

  return [{ ...newState, eventQueue: remaining }, toFire]
}
